import React, { useState } from "react";
import { Table, Select, Button, Spin } from "antd";
import { useGetRatiosQuery } from "../../services/analytics";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import * as XLSX from "xlsx";

const { Option } = Select;

const Ratios = () => {
    const [groupBy, setGroupBy] = useState("event");

    const { data: ratiosData, isFetching: isLoadingRatios } = useGetRatiosQuery({ groupBy });

    const tableData = ratiosData?.data || [];

    const handleExport = () => {
        if (!tableData || tableData.length === 0) return;
        const exportData = tableData.map(item => ({
            "Group By (Event/Job/Company)": item.group,
            "Total Requests": item.totalRequests,
            "Accepted": item.accepted,
            "Pending": item.pending,
            "Rejected": item.rejected,
            "Acceptance Rate %": Number(item.acceptanceRate).toFixed(2),
            "Rejection Rate %": Number(item.rejectionRate).toFixed(2)
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Acceptance Ratios");
        XLSX.writeFile(wb, `Acceptance_Ratios_${groupBy}.xlsx`);
    };

    const columns = [
        { title: 'Group', dataIndex: 'group', key: 'group' },
        { title: 'Total Requests', dataIndex: 'totalRequests', key: 'totalRequests', sorter: (a, b) => a.totalRequests - b.totalRequests },
        { title: 'Accepted', dataIndex: 'accepted', key: 'accepted' },
        { title: 'Pending', dataIndex: 'pending', key: 'pending' },
        { title: 'Rejected', dataIndex: 'rejected', key: 'rejected' },
        { title: 'Acceptance Rate', dataIndex: 'acceptanceRate', key: 'acceptanceRate', render: v => `${Number(v).toFixed(2)}%`, sorter: (a, b) => a.acceptanceRate - b.acceptanceRate },
        { title: 'Rejection Rate', dataIndex: 'rejectionRate', key: 'rejectionRate', render: v => `${Number(v).toFixed(2)}%`, sorter: (a, b) => a.rejectionRate - b.rejectionRate },
    ];

    // For chart, let's take top 10 groups by total requests to avoid overcrowding
    const chartData = tableData.slice(0, 10).map(item => ({
        name: item.group,
        Accepted: item.accepted,
        Rejected: item.rejected,
        Pending: item.pending
    }));

    return (
        <div className="animate-fade-in">
            <div className="mb-8 flex justify-between items-center bg-white p-5 rounded-2xl border border-gray-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]">
                <div>
                    <span className="mr-3 font-semibold text-gray-700">Group By:</span>
                    <Select
                        style={{ width: 250 }}
                        value={groupBy}
                        onChange={(val) => setGroupBy(val)}
                    >
                        <Option value="event">Event-wise</Option>
                        <Option value="job_title">Job-title-wise</Option>
                        <Option value="company">Company-wise</Option>
                    </Select>
                </div>
                <Button
                    type="primary"
                    disabled={!tableData || tableData.length === 0}
                    onClick={handleExport}
                    className="bg-primary hover:bg-yellow-600 border-none"
                >
                    Export Data
                </Button>
            </div>

            {isLoadingRatios ? (
                <div className="flex justify-center p-12"><Spin size="large" /></div>
            ) : tableData.length > 0 ? (
                <div className="space-y-8">
                    <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-shadow duration-300" style={{ height: 450 }}>
                        <h3 className="font-semibold text-xl text-gray-800 mb-6 border-b pb-4">Top 10 Meeting Interactions <span className="text-primary font-bold bg-yellow-50 px-3 py-1 rounded-full text-sm ml-2">({groupBy.replace('_', ' ').toUpperCase()})</span></h3>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 10, bottom: 40 }}>
                                <XAxis dataKey="name" tick={{ fill: '#8c8c8c', fontSize: 13, fontWeight: 500 }} angle={-30} textAnchor="end" height={80} axisLine={{ stroke: '#f0f0f0' }} tickLine={false} dy={15} />
                                <YAxis tick={{ fill: '#8c8c8c', fontSize: 13 }} axisLine={false} tickLine={false} dx={-10} />
                                <Tooltip cursor={{ fill: '#f0f2f5', opacity: 0.4 }} contentStyle={{ borderRadius: '12px', border: '1px solid #f0f0f0', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }} />
                                <Legend verticalAlign="top" height={36} wrapperStyle={{ paddingBottom: '20px' }} iconType="circle" />
                                <Bar dataKey="Accepted" stackId="a" fill="#52c41a" radius={[0, 0, 8, 8]} />
                                <Bar dataKey="Pending" stackId="a" fill="#faad14" />
                                <Bar dataKey="Rejected" stackId="a" fill="#ff4d4f" radius={[8, 8, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden">
                        <Table
                            dataSource={tableData}
                            columns={columns}
                            rowKey="group"
                            pagination={{ pageSize: 10, className: 'px-4 mb-4' }}
                            rowClassName="hover:bg-gray-50/50 transition-colors"
                        />
                    </div>
                </div>
            ) : (
                <div className="text-center text-gray-400 mt-20">
                    No data available for the selected grouping.
                </div>
            )}
        </div>
    );
};

export default Ratios;
